import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate, Outlet } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import PageLoader from '@/components/PageLoader';
import { AuthProvider, useAuth } from '@/lib/AuthContext';

import ProtectedRoute from '@/components/ProtectedRoute';
import { CartProvider } from '@/context/CartContext';
import { FavoritesProvider } from '@/context/FavoritesContext';
import CSLayout from '@/components/layout/CSLayout';
import CSHome from '@/pages/CSHome';
import ScrollToTop from '@/components/ScrollToTop';

const CSShop = lazy(() => import('@/pages/CSShop'));
const CSProductDetail = lazy(() => import('@/pages/CSProductDetail'));
const CSCheckout = lazy(() => import('@/pages/CSCheckout'));
const AdminProducts = lazy(() => import('@/pages/AdminProducts'));
const AdminOrders = lazy(() => import('@/pages/AdminOrders'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const AdminNewsletter = lazy(() => import('@/pages/AdminNewsletter'));
const CSFavorites = lazy(() => import('@/pages/CSFavorites'));
const CSOrders = lazy(() => import('@/pages/CSOrders'));
const CSProfile = lazy(() => import('@/pages/CSProfile'));
const CGVCS = lazy(() => import('@/pages/CGVCS'));
const MentionsLegales = lazy(() => import('@/pages/MentionsLegales'));
const APropos = lazy(() => import('@/pages/APropos'));
const Livraison = lazy(() => import('@/pages/Livraison'));
const PolitiqueConfidentialite = lazy(() => import('@/pages/PolitiqueConfidentialite'));
const Contact = lazy(() => import('@/pages/Contact'));
const SuiviCommande = lazy(() => import('@/pages/SuiviCommande'));
const Blog = lazy(() => import('@/pages/Blog'));
const BlogArticle = lazy(() => import('@/pages/BlogArticle'));
const AdminReviews = lazy(() => import('@/pages/AdminReviews'));
const AdminClients = lazy(() => import('@/pages/AdminClients'));
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const PageNotFound = lazy(() => import('./lib/PageNotFound'));
const UserNotRegisteredError = lazy(() => import('@/components/UserNotRegisteredError'));

const AuthenticatedApp = () => {
  const { authError } = useAuth();

  if (authError?.type === 'user_not_registered') {
    return <Suspense fallback={<PageLoader />}><UserNotRegisteredError /></Suspense>;
  }

  return (
    <CartProvider>
      <FavoritesProvider>
        <Routes>
          {/* Auth pages — sans CSLayout */}
          <Route element={<Suspense fallback={<PageLoader />}><Outlet /></Suspense>}>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
          </Route>

          {/* Pages avec CSLayout */}
          <Route element={<CSLayout />}>

            {/* Pages publiques */}
            <Route path="/" element={<CSHome />} />
            <Route path="/shop" element={<CSShop />} />
            <Route path="/product/:slug" element={<CSProductDetail />} />
            <Route path="/checkout" element={<CSCheckout />} />
            <Route path="/cgv" element={<CGVCS />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/a-propos" element={<APropos />} />
            <Route path="/livraison" element={<Livraison />} />
            <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/suivi-commande" element={<SuiviCommande />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />

            {/* Pages nécessitant une connexion */}
            <Route element={<ProtectedRoute unauthenticatedElement={<Navigate to="/login" replace />} />}>
              <Route path="/favoris" element={<CSFavorites />} />
              <Route path="/mes-commandes" element={<CSOrders />} />
              <Route path="/profil" element={<CSProfile />} />
            </Route>

            {/* Pages admin */}
            <Route element={<ProtectedRoute unauthenticatedElement={<Navigate to="/login" replace />} />}>
              <Route path="/admin/products" element={<AdminProducts />} />
              <Route path="/admin/orders" element={<AdminOrders />} />
              <Route path="/admin/clients" element={<AdminClients />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/newsletter" element={<AdminNewsletter />} />
              <Route path="/admin/reviews" element={<AdminReviews />} />
            </Route>

          </Route>

          <Route path="*" element={<Suspense fallback={<PageLoader />}><PageNotFound /></Suspense>} />
        </Routes>
      </FavoritesProvider>
    </CartProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <ScrollToTop />
          <AuthenticatedApp />
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  );
}

export default App;